import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Zap, Loader2, Save, Bot, Clock, Instagram, Facebook, ChevronDown, ChevronUp,
  CheckCircle2, AlertTriangle, SkipForward,
} from "lucide-react";

const REASONS = {
  article_published: "Noticia publicada",
  image_ready: "Placa generada",
  manual_run: "Ejecución manual",
  skipped: "Salteado",
};

const SKIP_REASONS = {
  disabled: "Auto-publicación desactivada",
  no_platforms_or_formats: "No hay plataformas o formatos elegidos",
  article_not_found: "Noticia no encontrada",
  no_images: "Sin placa generada",
};

const ARTICLE_STATUS_LABELS = {
  draft: "borrador",
  pending: "pendiente de revisión",
  scheduled: "programada",
  archived: "archivada",
  trash: "en papelera",
};

function humanizeSkip(reason) {
  if (!reason) return "";
  if (SKIP_REASONS[reason]) return SKIP_REASONS[reason];
  if (reason.startsWith("status=")) {
    const s = reason.slice(7);
    return `La noticia está ${ARTICLE_STATUS_LABELS[s] || s}`;
  }
  return reason;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" });
  } catch { return iso; }
}

export default function AdminAutoPublishCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.adminSocialAutoPublishGet(),
        api.adminSocialAutoPublishLog({ limit: 10 }),
      ]);
      setSettings(s);
      setLog(l?.items || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo cargar auto-publicación");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const next = await api.adminSocialAutoPublishSave(patch);
      setSettings(next);
      toast.success("Configuración guardada");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const togglePlatform = (p) => {
    if (!settings) return;
    const has = (settings.platforms || []).includes(p);
    const next = has ? settings.platforms.filter((x) => x !== p) : [...(settings.platforms || []), p];
    save({ platforms: next });
  };

  const toggleFormat = (f) => {
    if (!settings) return;
    const has = (settings.formats || []).includes(f);
    const next = has ? settings.formats.filter((x) => x !== f) : [...(settings.formats || []), f];
    save({ formats: next });
  };

  const setDelay = async (val) => {
    const n = Math.max(0, Math.min(3600, parseInt(val || "0", 10) || 0));
    save({ delay_seconds: n });
  };

  const isOn = !!settings?.enabled;

  return (
    <div
      data-testid="admin-auto-publish-card"
      className="border border-softblue bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 flex items-center justify-center border border-softblue ${isOn ? "bg-emerald-500" : "bg-slate-400"}`}>
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-serif text-lg font-bold text-blueblack">Auto-publicación</div>
            <div className="text-xs text-textgray">
              Publica automáticamente en redes cuando se publica una noticia con placa lista.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold ${isOn ? "text-emerald-700" : "text-textgray"}`}>
            {isOn ? "Activada" : "Desactivada"}
          </span>
          <Switch
            data-testid="auto-publish-enabled"
            checked={isOn}
            disabled={loading || saving}
            onCheckedChange={(v) => save({ enabled: !!v })}
          />
        </div>
      </div>

      {loading && !settings ? (
        <div className="text-center text-textgray text-sm py-6">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Cargando…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Platforms */}
            <div className="border border-softblue p-3">
              <div className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-2">Plataformas</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => togglePlatform("instagram")}
                  disabled={saving}
                  data-testid="auto-publish-platform-instagram"
                  className={`flex-1 border p-2 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
                    (settings.platforms || []).includes("instagram")
                      ? "border-pink-500 bg-pink-50 text-pink-800"
                      : "border-softblue text-textgray hover:bg-lightgray"
                  }`}
                >
                  <Instagram className="w-3.5 h-3.5" /> IG
                </button>
                <button
                  type="button"
                  onClick={() => togglePlatform("facebook")}
                  disabled={saving}
                  data-testid="auto-publish-platform-facebook"
                  className={`flex-1 border p-2 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
                    (settings.platforms || []).includes("facebook")
                      ? "border-[#1877F2] bg-blue-50 text-[#1877F2]"
                      : "border-softblue text-textgray hover:bg-lightgray"
                  }`}
                >
                  <Facebook className="w-3.5 h-3.5" /> FB
                </button>
              </div>
            </div>

            {/* Formats */}
            <div className="border border-softblue p-3">
              <div className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-2">Formatos</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => toggleFormat("post")}
                  disabled={saving}
                  data-testid="auto-publish-format-post"
                  className={`flex-1 border p-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
                    (settings.formats || []).includes("post")
                      ? "border-turquoise bg-turquoise/10 text-nightblue"
                      : "border-softblue text-textgray hover:bg-lightgray"
                  }`}
                >
                  Post
                </button>
                <button
                  type="button"
                  onClick={() => toggleFormat("story")}
                  disabled={saving}
                  data-testid="auto-publish-format-story"
                  className={`flex-1 border p-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
                    (settings.formats || []).includes("story")
                      ? "border-turquoise bg-turquoise/10 text-nightblue"
                      : "border-softblue text-textgray hover:bg-lightgray"
                  }`}
                >
                  Story
                </button>
              </div>
              <div className="text-[10px] text-textgray mt-2">Story: sólo Instagram (FB usa otra API).</div>
            </div>
          </div>

          {/* Extra options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            <div className="border border-softblue p-3">
              <label className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-1 block flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Retraso al disparar (segundos)
              </label>
              <input
                type="number"
                min={0}
                max={3600}
                defaultValue={settings.delay_seconds || 0}
                onBlur={(e) => setDelay(e.target.value)}
                data-testid="auto-publish-delay"
                className="w-full border border-softblue px-2 py-1 text-sm font-mono focus:outline-none focus:border-turquoise"
              />
              <div className="text-[10px] text-textgray mt-1">0 = inmediato. Máx 3600 (1 h).</div>
            </div>
            <div className="border border-softblue p-3">
              <label className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-2 block">
                Auto-generar placa si falta
              </label>
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="auto-publish-auto-generate"
                  checked={!!settings.auto_generate_placa}
                  disabled={saving}
                  onCheckedChange={(v) => save({ auto_generate_placa: !!v })}
                />
                <span className="text-xs text-textgray">
                  {settings.auto_generate_placa
                    ? "Sí — genera la placa POST automáticamente antes de publicar."
                    : "No — si no hay placa, el auto-publish se salta."}
                </span>
              </div>
            </div>
            <div className="border border-softblue p-3">
              <label className="text-[10px] uppercase tracking-widest text-textgray font-bold mb-2 block">
                Disparar cuando se genera la placa
              </label>
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="auto-publish-trigger-on-image"
                  checked={!!settings.trigger_on_image_ready}
                  disabled={saving}
                  onCheckedChange={(v) => save({ trigger_on_image_ready: !!v })}
                />
                <span className="text-xs text-textgray">
                  {settings.trigger_on_image_ready
                    ? "Sí — si la noticia ya está publicada al generar la placa, se auto-publica en redes."
                    : "No — sólo dispara al aprobar/publicar la noticia."}
                </span>
              </div>
            </div>
          </div>

          {/* Log */}
          <div className="mt-4 border border-softblue">
            <button
              type="button"
              onClick={() => setShowLog(!showLog)}
              data-testid="auto-publish-log-toggle"
              className="w-full flex items-center justify-between px-3 py-2 bg-lightgray hover:bg-softblue/20 text-xs uppercase tracking-widest font-bold text-blueblack"
            >
              <span>Últimas ejecuciones ({log.length})</span>
              {showLog ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showLog && (
              <div className="max-h-60 overflow-y-auto divide-y divide-softblue">
                {log.length === 0 && (
                  <div className="p-3 text-xs text-textgray text-center">Sin ejecuciones registradas todavía.</div>
                )}
                {log.map((e) => {
                  const isSkip = e.reason === "skipped" || (Array.isArray(e.results) && e.results.every((r) => r.status === "skipped" && !r.platform));
                  return (
                    <div key={e.id} className="p-3 text-xs" data-testid="auto-publish-log-row">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold text-blueblack line-clamp-1 flex-1">
                          {e.article_title || e.article_id?.slice(0, 8)}
                        </div>
                        <span className="text-[10px] uppercase tracking-widest text-textgray whitespace-nowrap">
                          {REASONS[e.reason] || e.reason} · {fmtDate(e.started_at)}
                        </span>
                      </div>
                      {isSkip && e.skip_reason ? (
                        <div data-testid="auto-publish-skip-chip" className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1 inline-flex items-start gap-1.5">
                          <SkipForward className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="uppercase font-bold text-[10px] mr-1">
                              {humanizeSkip(e.skip_reason)}
                            </span>
                            {e.skip_detail && <span className="normal-case"> — {e.skip_detail}</span>}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {(e.results || []).map((r, i) => {
                            const ok = r.status === "published" || r.status === "test_mode";
                            const skipped = r.status === "skipped";
                            const Icon = ok ? CheckCircle2 : (skipped ? SkipForward : AlertTriangle);
                            const cls = ok ? "text-emerald-700 border-emerald-300" :
                                         (skipped ? "text-amber-700 border-amber-300" : "text-red-700 border-red-300");
                            return (
                              <span key={i} className={`inline-flex items-center gap-1 border px-1.5 py-0.5 ${cls} text-[10px] uppercase font-bold`}>
                                <Icon className="w-3 h-3" />
                                {r.platform || "—"}
                                {r.error_code ? ` · ${r.error_code}` : ""}
                                {r.permalink && (
                                  <a href={r.permalink} target="_blank" rel="noopener noreferrer" className="underline ml-0.5 normal-case">ver</a>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button
              type="button"
              onClick={load}
              data-testid="auto-publish-refresh"
              variant="outline"
              disabled={loading || saving}
              className="h-9 rounded-none border-softblue text-nightblue text-[10px] uppercase tracking-widest font-bold"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
              Refrescar
            </Button>
            {saving && (
              <span className="text-[10px] uppercase tracking-widest text-textgray inline-flex items-center gap-1">
                <Save className="w-3 h-3" /> Guardando…
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
